import * as t from 'io-ts';
import { NotificationSubscriptionRecipientCodec } from './notification-subscription';

export const ManagedContainerStatusScopeCodec = t.keyof({
  ALL: null,
  ASSIGNED: null,
});
export const ManagedContainerStatusRelationshipCodec = t.keyof({
  PROVIDER_COMPANY: null,
  DELIVERY_SITE: null,
});
export const ManagedContainerStatusGroupByCodec = t.keyof({
  REGION: null,
  OPERATING_AREA: null,
});
export type ManagedContainerStatusScope = t.TypeOf<
  typeof ManagedContainerStatusScopeCodec
>;
export type ManagedContainerStatusRelationship = t.TypeOf<
  typeof ManagedContainerStatusRelationshipCodec
>;
export type ManagedContainerStatusGroupBy = t.TypeOf<
  typeof ManagedContainerStatusGroupByCodec
>;

/** Reserved until the report defines content customization fields. */
export type ManagedContainerStatusNotificationContentOptions = Record<
  string,
  never
>;
// t.record(t.string, t.never) doesn't typecheck (NeverC isn't a Mixed), so
// accept only the empty object directly.
const isEmptyContent = (
  u: unknown,
): u is ManagedContainerStatusNotificationContentOptions =>
  t.UnknownRecord.is(u) && Object.keys(u).length === 0;
const contentCodec = new t.Type<
  ManagedContainerStatusNotificationContentOptions,
  ManagedContainerStatusNotificationContentOptions,
  unknown
>(
  'ManagedContainerStatusNotificationContentOptions',
  isEmptyContent,
  (u, c) => (isEmptyContent(u) ? t.success(u) : t.failure(u, c)),
  t.identity,
);
const auditCodec = t.partial({
  createdById: t.string,
  lastModifiedById: t.string,
  createDttm: t.number,
  lastModifiedDttm: t.number,
});

export const ManagedContainerStatusNotificationOptionsCodec = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    regionId: t.string,
    operatorCompanyId: t.string,
    category: t.keyof({ WATER: null, OIL: null }),
    scopeMode: ManagedContainerStatusScopeCodec,
    groupBy: ManagedContainerStatusGroupByCodec,
    enabled: t.boolean,
    sendEmptyReports: t.boolean,
  }),
  t.partial({
    recipientCompanyId: t.string,
    assignmentRelationship: ManagedContainerStatusRelationshipCodec,
    assignmentTargetCompanyId: t.string,
    assignmentPolicyKey: t.string,
    scheduleCron: t.string,
    scheduleTimezone: t.string,
    contentOptions: contentCodec,
  }),
  auditCodec,
]);
export type ManagedContainerStatusNotificationOptions = t.TypeOf<
  typeof ManagedContainerStatusNotificationOptionsCodec
>;
export type ManagedContainerStatusNotificationOptionsSave = Omit<
  ManagedContainerStatusNotificationOptions,
  | 'id'
  | 'recipientCompanyId'
  | 'assignmentRelationship'
  | 'assignmentTargetCompanyId'
  | 'assignmentPolicyKey'
  | 'scheduleCron'
  | 'scheduleTimezone'
> & {
  id?: string;
  recipientCompanyId: string | null;
  assignmentRelationship: ManagedContainerStatusRelationship | null;
  assignmentTargetCompanyId: string | null;
  assignmentPolicyKey: string | null;
  scheduleCron: string | null;
  scheduleTimezone: string | null;
};

export const ManagedContainerStatusNotificationSubscriptionCodec =
  t.intersection([
    t.type({
      id: t.string,
      optionsId: t.string,
      email: t.boolean,
      sms: t.boolean,
      enabled: t.boolean,
    }),
    NotificationSubscriptionRecipientCodec,
    t.partial({ contentOverrides: contentCodec }),
    auditCodec,
  ]);
export type ManagedContainerStatusNotificationSubscription = t.TypeOf<
  typeof ManagedContainerStatusNotificationSubscriptionCodec
>;
export type ManagedContainerStatusNotificationSubscriptionSave = Pick<
  ManagedContainerStatusNotificationSubscription,
  'email' | 'sms' | 'enabled' | 'contentOverrides'
> & { personId: string | null; notificationEndpointId: string | null };

export const managedContainerStatusAssignmentPolicies = {
  PROVIDER_COMPANY: [
    { value: 'top-provider', label: 'Top assigned provider' },
    { value: 'any-provider', label: 'Any assigned provider' },
  ],
  DELIVERY_SITE: [
    { value: 'top-delivery-site', label: 'Top assigned delivery site' },
    { value: 'any-delivery-site', label: 'Any assigned delivery site' },
  ],
} as const;
